import { ButtonFrame, Card, CardSearch, PageBodyHeader, Table } from "../../component";
import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { checkStoreByStoreIdUserId, dateToSystemTz, fetchOrderRequestById, fetchOrderTrackerDetail} from "../../action/generalFunc";
import { generalObj } from "../../constant/object_types";
import ModalParent from "../../component/modal/ModalParent";

const statusBadgeColor = (status:string) => {
    switch (status.toLowerCase()) {
        case "fulfilled":
            return { backgroundColor: "#E4F7EA", color: "#00983A" };
        case "cancelled":
            return { backgroundColor: "#FCE4E4", color: "#EB212F" };
        case "pending":
            return { backgroundColor: "#FFF3D6", color: "#B5820B" };
        default:
            return { backgroundColor: "#E9EAEB", color: "#676967" };
    }
};

const AdminOrderTrackerDetail = () => {
    const { date, supervisor_id, store_id, vendor_id } = useParams();
    const account = useSelector((state:RootState) => state.account);
    const { setModalLoading }: { setModalLoading:Function } = useOutletContext();

    const [searchText, setSearchText] = useState<string>("");
    const [storeName, setStoreName] = useState<string>("");
    const [vendorName, setVendorName] = useState<string>("");
    const [orderTrackerDetails, setOrderTrackerDetails] = useState<generalObj[]>([]);
    const [viewOrderRequest, setViewOrderRequest] = useState<generalObj | null>(null);
    const [openViewModal, setOpenViewModal] = useState<boolean>(false);
    
    useEffect(() => {
        if (!account || !account.isAuthenticated || !supervisor_id || !store_id || !vendor_id) return;

        const loadForm = async () => {
            setModalLoading(true);
            const storeResult = await checkStoreByStoreIdUserId(account.token, store_id, vendor_id);
            if (!storeResult) {
                window.history.back();
                setModalLoading(false);
                return;
            }

            setStoreName(`${storeResult.data.store_data.name} - ${storeResult.data.store_data.organization}`);
            setVendorName(storeResult.data.vendor_data.username);
            const trackerDetailResult = await fetchOrderTrackerDetail(account.token, vendor_id, store_id, (date || "").replaceAll("-", "_"));
            
            setOrderTrackerDetails(trackerDetailResult || []);
            
            setModalLoading(false);
        };

        loadForm();
    }, [account, supervisor_id, store_id, vendor_id, date, setModalLoading]);
        
    const formatMonth = (month:string|undefined) => {
        if (!month) return "-";
        const [year, monthNumber] = month.split("_");
        const monthDate = new Date(Number(year), Number(monthNumber) - 1);
        return Number.isNaN(monthDate.getTime()) ? month : `${monthDate.toLocaleString("en-US", {month:"short"})} ${year}`;
    };

    const formatNumber = (value:number | string | undefined | null) => value === undefined || value === null ? "-" : Number(value).toLocaleString("en-US", {maximumFractionDigits: 2});

    const getOrderRequestId = (order:generalObj) => order.order_request?.id || order.order_request_id || (typeof order.order_request === "number" || typeof order.order_request === "string" ? order.order_request : null);

    const viewOrderRequestDetail = async (orderRequestId:number | string) => {
        if (!account || !account.isAuthenticated) return;
        setModalLoading(true);
        const orderRequestResult = await fetchOrderRequestById(account.token, orderRequestId);
        if (orderRequestResult) {
            setViewOrderRequest(orderRequestResult);
            setOpenViewModal(true);
        }
        setModalLoading(false);
    };

    return (
        <>
            <PageBodyHeader title={`Order Tracker for ${formatMonth(date)}`} desc="Track your store's order requests for a specific date"
                inlineChild={<ButtonFrame btn_text="Back" btn_style={{padding:"8px 24px", borderRadius:"8px", border:"1px solid #808280",  backgroundColor:"#808280", color:"#FFF", fontSize:"14px", fontWeight:"600", letterSpacing:"-0.4px"}} onPress={() => window.history.back()} />}
            />

            <div style={{marginTop:"30px"}}>
                <Table table_head={["#", "Order Date", "Order request ID", "PO Number", "Total Value", "Fulfilled Value", "Fulfillment Date", "Status", "Action"]} th_class="white" color="#1159AB" thColor="#1159AB" tableSearch={<CardSearch value={searchText} onTextChange={(value) => setSearchText(value)} />} table_title={`Order tracker for ${vendorName.toUpperCase()} in ${storeName}`}>
                    <>
                        {orderTrackerDetails.filter((order) => `${order.order_request?.order_request_number} ${order.po_number} ${order.status}`.toLowerCase().includes(searchText.toLowerCase())).map((order, index) => (
                            <tr key={order.id || index}>
                                <td>{index + 1}</td>
                                <td>{dateToSystemTz(order.order_date)}</td>
                                <td>{order.order_request?.order_request_number || "-"}</td>
                                <td>{order.po_number || "-"}</td>
                                <td>{formatNumber(order.order_value)}</td>
                                <td>{formatNumber(order.fulfilled_order_value)}</td>
                                <td>{order.fulfillment_date ? dateToSystemTz(order.fulfillment_date) : "-"}</td>
                                <td><span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", textTransform:"capitalize", ...statusBadgeColor(order.status || "")}}>{order.status || "-"}</span></td>
                                <td>
                                    <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                                        {getOrderRequestId(order) && <button type="button" onClick={() => viewOrderRequestDetail(getOrderRequestId(order))} style={{padding:"6px 12px", borderRadius:"6px", border:"1px solid #1159AB", color:"#1159AB", fontWeight:"600"}}>View Request</button>}
                                        { order.po_file && (
                                            <div>
                                                <a href={order.po_file} target="_blank" rel="noreferrer">View PO</a>
                                            </div>
                                            )
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </>
                </Table>
            </div>

            <ModalParent visible={openViewModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"700px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>{`Order Request #${viewOrderRequest?.request_number || viewOrderRequest?.order_number || viewOrderRequest?.order_request_number || viewOrderRequest?.id || ""}`}</h4>
                            <button type="button" title="Close" onClick={() => setOpenViewModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{padding:"20px"}}>
                                <div style={{display:"flex", gap:"20px", justifyContent:"space-between", flexWrap:"wrap", marginBottom:"20px"}}>
                                    <span>{`Date: ${dateToSystemTz(viewOrderRequest?.created_at || viewOrderRequest?.order_made_date || viewOrderRequest?.date)}`}</span>
                                    <span>{`Fulfilled Date: ${viewOrderRequest?.fulfilled_at ?dateToSystemTz(viewOrderRequest?.fulfilled_at) : "-"}`}</span>
                                    <span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", ...statusBadgeColor(viewOrderRequest?.status || viewOrderRequest?.order_status || "Pending")}}>{viewOrderRequest?.status || viewOrderRequest?.order_status || "Pending"}</span>
                                </div>
                                <table className="tableComponent" style={{width:"100%"}}>
                                    <thead>
                                        <tr>
                                            <th className="headerText white">Product</th>
                                            <th className="headerText white">Quantity</th>
                                            <th className="headerText white">Received Quantity</th>
                                            <th className="headerText white">Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(viewOrderRequest?.items || []).map((item:generalObj, itemIndex:number) => (
                                            <tr key={item.id || itemIndex}>
                                                <td>{item.product?.name || item.product_name || item.product || "-"}</td>
                                                <td>{item.requested_quantity ?? item.quantity ?? "-"}</td>
                                                <td>{item.received_quantity ?? "-"}</td>
                                                <td>{item.unit?.label || item.unit_name || item.unit || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>
        </>
    )
}

export default AdminOrderTrackerDetail;